import { BookingStateRedux } from "./states";
import { PayloadAction } from "@reduxjs/toolkit";
import { language } from "../../@types/DTO/AppSetting";

export default {
  // application settings
  setCurrency(state, value) {
    state.currency = value;
  },
  setIsActive(state, value) {
    state.isActive = value;
  },
  setIsEmbedded(state, { payload: value }) {
    state.isEmbedded = value;
    // console.log(value);
  },
  setIsLogged(state, value) {
    state.isLoggedIn = !!value;
  },
  setRentalTerms(state, { payload: value }) {
    state.rentalTerms = value;
  },

  // APIs
  setBookingInformation(
    state,
    { payload: value }: PayloadAction<BookingStateRedux["bookingInformation"]>
  ) {
    state.bookingInformation = {
      ...value,
      dropOffDateAndTime: new Date(
        value.dropOffDateAndTime || Date.now()
      ).getTime(),
      pickUpDateAndTime: new Date(
        value.pickUpDateAndTime || Date.now()
      ).getTime(),
      // Preserve branch map search data — these are managed separately via
      // setBranchMapSearch / clearBranchMapSearch and must not be wiped when
      // the booking form is submitted and the user navigates back.
      branchSearchAddress: state.bookingInformation.branchSearchAddress ?? "",
      branchAddressCoords: state.bookingInformation.branchAddressCoords ?? null,
      branchDistanceMap: state.bookingInformation.branchDistanceMap ?? {},
      branchMapPanelState: state.bookingInformation.branchMapPanelState ?? 1,
      branchSelectedLocationCode:
        state.bookingInformation.branchSelectedLocationCode ?? null,
      dropoffSearchAddress: state.bookingInformation.dropoffSearchAddress ?? "",
      dropoffAddressCoords:
        state.bookingInformation.dropoffAddressCoords ?? null,
      dropoffDistanceMap: state.bookingInformation.dropoffDistanceMap ?? {},
      dropoffMapPanelState: state.bookingInformation.dropoffMapPanelState ?? 1,
      dropoffSelectedLocationCode:
        state.bookingInformation.dropoffSelectedLocationCode ?? null,
    } as BookingStateRedux["bookingInformation"];
  },
  setSessionExpiryTime(
    state: BookingStateRedux,
    { payload: value }: PayloadAction<BookingStateRedux["sessionExpiryTime"]>
  ) {
    state.sessionExpiryTime = value;
  },
  setSelectedVehicleDetails(
    state: BookingStateRedux,
    {
      payload: value,
    }: PayloadAction<BookingStateRedux["selectedVehicleDetails"]>
  ) {
    state.selectedVehicleDetails = {
      vehicleDetails: {
        optionals: [],
        Status: "Unavailable",
        TotalCharge: null,
        Vehicle: undefined,
      },

      paymentDetails: {
        // ...value.paymentDetails,
        selectedInsurance: value.paymentDetails?.selectedInsurance || {
          price: 0,
          details: "",
          name: "insurance",
        },
        addedExtraOptions: value.paymentDetails?.addedExtraOptions || {},
        type: value.paymentDetails?.type || "PAYNOW",
        price: ((value.paymentDetails?.price || 0) * 100) / 100,
      },
      ...value,
    };
  },
  setUserDetails(
    state: BookingStateRedux,
    { payload: value }: PayloadAction<BookingStateRedux["userDetails"]>
  ) {
    state.userDetails = value;
  },
  setAppSetting(
    state: BookingStateRedux,
    { payload: value }: PayloadAction<BookingStateRedux["appSetting"]>
  ) {
    state.appSetting = value;
  },

  setTextResource(
    state: BookingStateRedux,
    { payload: value }: PayloadAction<BookingStateRedux["textresource"]>
  ) {
    state.textresource = value;
  },

  setCustomerPrimaryDetails(
    state: BookingStateRedux,
    {
      payload: value,
    }: PayloadAction<BookingStateRedux["customerPrimaryDetails"]>
  ) {
    state.customerPrimaryDetails = value;
  },
  setAffiliateLocationDetails(
    state: BookingStateRedux,
    { payload }: PayloadAction<BookingStateRedux["affiliateLocationDetails"]>
  ) {
    state.affiliateLocationDetails = {
      ...state.affiliateLocationDetails,
      ...payload, // Merge new affiliateId data with existing
    };
  },
  setTextResourceDetailsBasedOnLang(
    state: BookingStateRedux,
    { payload }: PayloadAction<BookingStateRedux["textResourceBasedOnLang"]>
  ) {
    state.textResourceBasedOnLang = {
      ...state.textResourceBasedOnLang,
      ...payload, // Merge existing and new text resources
    };
  },

  // Branch map search — stores the address, coords, distance map and map state
  // set by BranchAddressSearch when showGoogleMapsPanel is enabled.
  setBranchMapSearch(
    state: BookingStateRedux,
    {
      payload,
    }: PayloadAction<{
      branchSearchAddress: string;
      branchAddressCoords: { lat: number; lng: number } | null;
      branchDistanceMap: Record<string, number>;
      branchMapPanelState: 1 | 2 | 3;
      branchSelectedLocationCode?: string | null;
      dropoffSearchAddress?: string;
      dropoffAddressCoords?: { lat: number; lng: number } | null;
      dropoffDistanceMap?: Record<string, number>;
      dropoffMapPanelState?: 1 | 2 | 3;
      dropoffSelectedLocationCode?: string | null;
    }>
  ) {
    state.bookingInformation = {
      ...state.bookingInformation,
      branchSearchAddress: payload.branchSearchAddress,
      branchAddressCoords: payload.branchAddressCoords,
      branchDistanceMap: payload.branchDistanceMap,
      branchMapPanelState: payload.branchMapPanelState,
      branchSelectedLocationCode: payload.branchSelectedLocationCode ?? null,
      ...(payload.dropoffSearchAddress !== undefined && {
        dropoffSearchAddress: payload.dropoffSearchAddress,
      }),
      ...(payload.dropoffAddressCoords !== undefined && {
        dropoffAddressCoords: payload.dropoffAddressCoords,
      }),
      ...(payload.dropoffDistanceMap !== undefined && {
        dropoffDistanceMap: payload.dropoffDistanceMap,
      }),
      ...(payload.dropoffMapPanelState !== undefined && {
        dropoffMapPanelState: payload.dropoffMapPanelState,
      }),
      ...(payload.dropoffSelectedLocationCode !== undefined && {
        dropoffSelectedLocationCode: payload.dropoffSelectedLocationCode,
      }),
    };
  },

  clearBranchMapSearch(state: BookingStateRedux) {
    state.bookingInformation = {
      ...state.bookingInformation,
      branchSearchAddress: "",
      branchAddressCoords: null,
      branchDistanceMap: {},
      branchMapPanelState: 1,
      branchSelectedLocationCode: null,
      dropoffSearchAddress: "",
      dropoffAddressCoords: null,
      dropoffDistanceMap: {},
      dropoffMapPanelState: 1,
      dropoffSelectedLocationCode: null,
    };
  },

  setDropoffMapSearch(
    state: BookingStateRedux,
    {
      payload,
    }: PayloadAction<{
      dropoffSearchAddress: string;
      dropoffAddressCoords: { lat: number; lng: number } | null;
      dropoffDistanceMap: Record<string, number>;
      dropoffMapPanelState: 1 | 2 | 3;
      dropoffSelectedLocationCode?: string | null;
    }>
  ) {
    state.bookingInformation = {
      ...state.bookingInformation,
      dropoffSearchAddress: payload.dropoffSearchAddress,
      dropoffAddressCoords: payload.dropoffAddressCoords,
      dropoffDistanceMap: payload.dropoffDistanceMap,
      dropoffMapPanelState: payload.dropoffMapPanelState,
      dropoffSelectedLocationCode: payload.dropoffSelectedLocationCode ?? null,
    };
  },

  clearDropoffMapSearch(state: BookingStateRedux) {
    state.bookingInformation = {
      ...state.bookingInformation,
      dropoffSearchAddress: "",
      dropoffAddressCoords: null,
      dropoffDistanceMap: {},
      dropoffMapPanelState: 1,
      dropoffSelectedLocationCode: null,
    };
  },
};
